import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ShieldAlert, Loader2, RefreshCw, Trash2, Mail, X, Send, ShieldCheck,
  CheckSquare, Square,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../components/ConfirmDialog';
import {
  adminListReports,
  adminDeletePrayer,
  adminBulkDeletePrayers,
  adminDismissReports,
  adminBulkDismissReports,
  adminNotifyUser,
} from '../lib/api';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

/**
 * Admin Misuse Reports triage (`/admin/misuse-reports`). Reads per-prayer
 * reports from `GET /api/admin/reports` (aggregated from the
 * `prayer_reports` collection) and gives moderators three per-row actions
 * and two multi-select bulk actions:
 *
 *  Per-row:
 *   - Delete Prayer  → prompts for a reason, then `POST /api/admin/prayers/{id}/delete`
 *     with `{reason}` → soft-deletes + clears open reports on the prayer +
 *     stamps `deleted_reason` on the audit trail. The reason is cached
 *     locally so the Notify Owner modal can prefill it into the message.
 *   - Delete Report  → `DELETE /api/admin/reports/{id}` — dismisses the
 *     reports WITHOUT touching the prayer.
 *   - Notify Owner   → `POST /api/admin/users/{owner_id}/notify` (writes
 *     an `admin_notify_user` direct notification).
 *
 *  Bulk (when one or more rows are checked):
 *   - Delete Prayers (bulk) → prompts for one shared reason → batch soft-delete
 *   - Delete Reports (bulk) → dismisses reports on all selected prayers
 */

const DeleteReasonModal = ({ open, count, working, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!open) return null;
  const isBulk = count > 1;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="delete-reason-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        data-testid="delete-reason-dialog"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200 dark:border-zinc-800">
          <Trash2 size={16} className="text-red-600" />
          <h2 className="text-sm font-semibold">
            {isBulk ? `Delete ${count} prayers?` : 'Delete prayer?'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="delete-reason-close-btn"
            className="ml-auto p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            This soft-deletes {isBulk ? `${count} prayers` : 'the prayer'} and clears related reports. Please provide a short reason (used in the audit trail and pre-filled into Notify Owner).
          </p>
          <textarea
            rows={4}
            data-testid="delete-reason-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
            placeholder="e.g. Spam content, harassment, off-topic…"
            autoFocus
            className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          <div className="text-right text-xs text-zinc-500 tabular-nums">{reason.length}/500</div>
        </div>
        <div className="px-4 py-3 border-t border-zinc-200 dark:border-zinc-800 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="delete-reason-cancel-btn"
            className="h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={working || !reason.trim()}
            data-testid="delete-reason-confirm-btn"
            className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {working ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {isBulk ? `Delete ${count}` : 'Delete Prayer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const NotifyOwnerModal = ({ open, row, storedReason, sending, onClose, onSend }) => {
  const [msg, setMsg] = useState('');

  const title = row?.prayer?.title || '(untitled)';
  const authorReason = storedReason || row?.latest_reason || '';
  const suggested = row?.prayer?.is_deleted
    ? `Hi, your prayer "${title}" was removed after admin review.${authorReason ? ` Reason: ${authorReason}.` : ''} If you believe this is a mistake, please reach out via Send feedback.`
    : `Hi, your prayer "${title}" was flagged by the community.${authorReason ? ` Reported reason: ${authorReason}.` : ''} Please review our guidelines and consider revising it.`;

  useEffect(() => {
    if (open) setMsg(suggested);
  }, [open, row?.prayer_id, storedReason, suggested]);

  if (!open || !row) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="notify-owner-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        data-testid="notify-owner-dialog"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200 dark:border-zinc-800">
          <Mail size={16} className="text-amber-600" />
          <h2 className="text-sm font-semibold">Notify prayer owner</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="notify-owner-close-btn"
            className="ml-auto p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-zinc-500 space-y-0.5">
            <div>
              Owner: <span className="font-medium text-zinc-700 dark:text-zinc-200">
                {row.prayer?.owner_missing
                  ? '(owner account deleted)'
                  : (row.prayer?.author_name
                      || (row.prayer?.author_handle ? `@${row.prayer.author_handle}` : '(unknown)'))}
                {!row.prayer?.owner_missing && row.prayer?.author_handle && row.prayer?.author_name
                  ? ` · @${row.prayer.author_handle}` : ''}
              </span>
            </div>
            <div>
              Prayer: <span className="italic">{title}</span>
            </div>
            {storedReason && (
              <div>Removal reason: <span className="italic">{storedReason}</span></div>
            )}
            {!storedReason && row.latest_reason && (
              <div>Latest report reason: <span className="italic">{row.latest_reason}</span></div>
            )}
          </div>
          <textarea
            rows={5}
            data-testid="notify-owner-message"
            value={msg}
            onChange={(e) => setMsg(e.target.value.slice(0, 1000))}
            placeholder={suggested}
            className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setMsg(suggested)}
              data-testid="notify-owner-suggest-btn"
              className="text-xs text-amber-600 hover:underline"
            >
              Use suggested message
            </button>
            <span className="text-xs text-zinc-500 tabular-nums">{msg.length}/1000</span>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-zinc-200 dark:border-zinc-800 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="notify-owner-cancel-btn"
            className="h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSend((msg || suggested).trim())}
            disabled={sending || !((msg || suggested).trim())}
            data-testid="notify-owner-send-btn"
            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send notification
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminMisuseReports = () => {
  const { user, loading: authLoading } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);
  const [notifyRow, setNotifyRow] = useState(null);
  const [notifySending, setNotifySending] = useState(false);
  const [banner, setBanner] = useState(null);
  // Row selection (set of prayer_ids) for bulk actions
  const [selected, setSelected] = useState(new Set());
  // Delete-reason modal state: {ids: [prayerId,...], mode: 'single' | 'bulk'}
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Bulk dismiss loading + last-used delete reasons keyed by prayer_id so
  // the Notify Owner suggestion can reference the actual removal reason.
  const [bulkDismissing, setBulkDismissing] = useState(false);
  const [reasonByPrayerId, setReasonByPrayerId] = useState({});

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const data = await adminListReports();
      setRows(Array.isArray(data) ? data : []);
      setSelected(new Set());
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load misuse reports');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  const rowById = useMemo(() => {
    const map = {};
    rows.forEach(r => { map[r.prayer_id] = r; });
    return map;
  }, [rows]);

  const toggleOne = (prayerId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(prayerId)) next.delete(prayerId); else next.add(prayerId);
      return next;
    });
  };

  const allIds = useMemo(() => rows.map(r => r.prayer_id), [rows]);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allIds));
  };

  // --- Delete flow (both single and bulk go through the same modal) ---
  const openDeletePrompt = (ids) => {
    if (!ids.length) return;
    setPendingDelete({ ids, mode: ids.length === 1 ? 'single' : 'bulk' });
  };
  const confirmDelete = async (reason) => {
    const ids = pendingDelete?.ids || [];
    if (!ids.length) return;
    setDeleting(true); setBanner(null);
    try {
      if (ids.length === 1) {
        await adminDeletePrayer(ids[0], reason);
      } else {
        await adminBulkDeletePrayers(ids, reason);
      }
      // Cache reason for these ids so Notify Owner can prefill it later.
      setReasonByPrayerId((prev) => {
        const next = { ...prev };
        ids.forEach(id => { next[id] = reason; });
        return next;
      });
      // Reflect in-list: mark prayers as deleted (single) or drop them (bulk)
      setRows((prev) => prev.map((r) => (
        ids.includes(r.prayer_id)
          ? { ...r, prayer: { ...(r.prayer || {}), is_deleted: true } }
          : r
      )));
      setSelected((prev) => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      setBanner({
        kind: 'ok',
        text: ids.length === 1
          ? `Deleted "${rowById[ids[0]]?.prayer?.title || '(untitled)'}".`
          : `Deleted ${ids.length} prayers.`,
      });
      setPendingDelete(null);
    } catch (e) {
      setBanner({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Delete failed' });
    } finally {
      setDeleting(false);
    }
  };

  // --- Dismiss flow (report-only, no reason needed) ---
  const handleDismiss = async (row) => {
    const title = row.prayer?.title || '(untitled)';
    const ok = await confirm({
      title: `Dismiss ${row.report_count} report${row.report_count === 1 ? '' : 's'}?`,
      description: `The prayer "${title}" will REMAIN live. Only the report entries will be removed from the queue.`,
      confirmText: 'Dismiss reports',
      cancelText: 'Cancel',
      destructive: false,
    });
    if (!ok) return;
    setDismissingId(row.prayer_id);
    setBanner(null);
    try {
      const res = await adminDismissReports(row.prayer_id);
      setRows((prev) => prev.filter((r) => r.prayer_id !== row.prayer_id));
      setSelected((prev) => { const next = new Set(prev); next.delete(row.prayer_id); return next; });
      setBanner({ kind: 'ok', text: `Dismissed ${res?.removed ?? row.report_count} report${(res?.removed ?? row.report_count) === 1 ? '' : 's'} against "${title}". Prayer left untouched.` });
    } catch (e) {
      setBanner({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Dismiss failed' });
    } finally {
      setDismissingId(null);
    }
  };

  const handleBulkDismiss = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const ok = await confirm({
      title: `Dismiss reports on ${ids.length} prayer${ids.length === 1 ? '' : 's'}?`,
      description: 'The prayers will REMAIN live — only the report entries will be cleared from the queue.',
      confirmText: 'Dismiss all',
      cancelText: 'Cancel',
      destructive: false,
    });
    if (!ok) return;
    setBulkDismissing(true); setBanner(null);
    try {
      const res = await adminBulkDismissReports(ids);
      setRows((prev) => prev.filter((r) => !ids.includes(r.prayer_id)));
      setSelected(new Set());
      setBanner({ kind: 'ok', text: `Dismissed ${res?.removed ?? 0} report entr${(res?.removed ?? 0) === 1 ? 'y' : 'ies'} across ${ids.length} prayer${ids.length === 1 ? '' : 's'}. Prayers left untouched.` });
    } catch (e) {
      setBanner({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Bulk dismiss failed' });
    } finally {
      setBulkDismissing(false);
    }
  };

  const handleNotify = async (message) => {
    const row = notifyRow;
    const ownerId = row?.prayer?.user_id;
    if (!ownerId) {
      setBanner({ kind: 'err', text: 'Missing owner id — cannot notify.' });
      return;
    }
    setNotifySending(true);
    setBanner(null);
    try {
      await adminNotifyUser(ownerId, message, { prayer_id: row.prayer_id });
      // Optimistically bump the row's notify_count so the user sees the
      // updated tally before the next refresh.
      setRows((prev) => prev.map((rr) => (
        rr.prayer_id === row.prayer_id
          ? { ...rr, notify_count: (rr.notify_count || 0) + 1, notify_last_at: new Date().toISOString() }
          : rr
      )));
      setBanner({
        kind: 'ok',
        text: `Notification sent to ${row.prayer?.author_handle ? '@' + row.prayer.author_handle : (row.prayer?.author_name || 'the owner')}.`,
      });
      setNotifyRow(null);
    } catch (e) {
      setBanner({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Notify failed' });
    } finally {
      setNotifySending(false);
    }
  };

  if (authLoading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /> Loading…</div>;
  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center" data-testid="admin-forbidden">
        <ShieldAlert size={40} className="mx-auto text-red-500 mb-3" />
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <ShieldAlert size={22} className="text-red-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-misuse-title">Admin · Misuse Reports</h1>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="refresh-misuse-btn"
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {banner && (
        <div
          data-testid={banner.kind === 'ok' ? 'admin-misuse-success' : 'admin-misuse-error'}
          className={`p-3 rounded-lg text-sm mb-4 ${
            banner.kind === 'ok'
              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          }`}
        >{banner.text}</div>
      )}
      {err && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4" data-testid="admin-misuse-load-error">{err}</div>}

      {/* Bulk selection toolbar — shown whenever there are rows */}
      {rows.length > 0 && (
        <div
          data-testid="admin-misuse-bulk-toolbar"
          className="flex items-center gap-3 mb-3 px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50 sticky top-0 z-10"
        >
          <button
            type="button"
            onClick={toggleAll}
            data-testid="misuse-select-all-btn"
            aria-label={allSelected ? 'Deselect all' : 'Select all'}
            className="flex items-center gap-2 text-sm font-medium hover:opacity-80"
          >
            {allSelected
              ? <CheckSquare size={18} className="text-amber-600" />
              : someSelected
                ? <div className="w-[18px] h-[18px] rounded-sm bg-amber-500/60 border-2 border-amber-600" />
                : <Square size={18} className="text-zinc-400" />}
            <span className="text-zinc-700 dark:text-zinc-200">
              {selected.size === 0 ? 'Select all' : `${selected.size} selected`}
            </span>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={handleBulkDismiss}
              disabled={selected.size === 0 || bulkDismissing}
              data-testid="misuse-bulk-dismiss-btn"
              title="Dismiss all selected reports (leave prayers live)"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {bulkDismissing ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              Delete Reports {selected.size ? `(${selected.size})` : ''}
            </button>
            <button
              type="button"
              onClick={() => openDeletePrompt(Array.from(selected))}
              disabled={selected.size === 0}
              data-testid="misuse-bulk-delete-btn"
              title="Delete all selected prayers (with a shared reason)"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 size={14} />
              Delete Prayers {selected.size ? `(${selected.size})` : ''}
            </button>
          </div>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading misuse reports…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-zinc-500" data-testid="admin-misuse-empty">No prayers have been reported.</div>
      ) : (
        <ul className="space-y-3" data-testid="admin-misuse-list">
          {rows.map((r, i) => {
            const isDeleted = r.prayer?.is_deleted;
            const canNotify = !!r.prayer?.user_id && !r.prayer?.owner_missing;
            const isSelected = selected.has(r.prayer_id);
            return (
              <li
                key={r.prayer_id || `misuse-${i}`}
                data-testid={`misuse-row-${i}`}
                data-prayer-id={r.prayer_id}
                className={`p-4 rounded-xl border transition-colors ${
                  isSelected
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-600'
                    : 'border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => toggleOne(r.prayer_id)}
                    data-testid={`misuse-select-${i}`}
                    aria-label={isSelected ? 'Deselect row' : 'Select row'}
                    className="mt-1 shrink-0 hover:opacity-80"
                  >
                    {isSelected
                      ? <CheckSquare size={20} className="text-amber-600" />
                      : <Square size={20} className="text-zinc-400" />}
                  </button>

                  <div className="flex-1 min-w-0 flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/prayer/${r.prayer_id}`}
                          data-testid={`misuse-row-${i}-title-link`}
                          className="font-semibold text-zinc-900 dark:text-white hover:underline truncate"
                        >
                          {r.prayer?.title || '(untitled)'}
                        </Link>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-300 font-medium">
                          {r.report_count} report{r.report_count === 1 ? '' : 's'}
                        </span>
                        {(r.notify_count || 0) > 0 && (
                          <span
                            data-testid={`misuse-row-${i}-notify-count`}
                            title={r.notify_last_at ? `Last notified ${fmtDateTime(r.notify_last_at)}` : 'Owner has been notified'}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-medium"
                          >
                            <Mail size={10} />
                            {r.notify_count} notices sent
                          </span>
                        )}
                        {isDeleted && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-200 dark:bg-zinc-800 text-zinc-600" data-testid={`misuse-row-${i}-deleted-badge`}>deleted</span>
                        )}
                      </div>
                      {(r.prayer?.author_handle || r.prayer?.author_name || r.prayer?.owner_missing) && (
                        <div className="text-xs text-zinc-500 mt-0.5">
                          {r.prayer?.owner_missing
                            ? 'by (owner account deleted)'
                            : (
                              <>
                                by {r.prayer.author_name || (r.prayer.author_handle ? `@${r.prayer.author_handle}` : '')}
                                {r.prayer.author_name && r.prayer.author_handle ? ` · @${r.prayer.author_handle}` : ''}
                              </>
                            )}
                        </div>
                      )}
                      {r.prayer?.description && (
                        <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-2 line-clamp-3 whitespace-pre-wrap">
                          {r.prayer.description}
                        </p>
                      )}
                      {r.latest_reason && (
                        <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 border-l-2 border-zinc-300 dark:border-zinc-700 pl-2 italic">
                          Latest reason: {r.latest_reason}
                        </div>
                      )}
                      {reasonByPrayerId[r.prayer_id] && (
                        <div className="mt-1 text-xs text-red-600 dark:text-red-400 border-l-2 border-red-400 pl-2 italic" data-testid={`misuse-row-${i}-removal-reason`}>
                          Removal reason: {reasonByPrayerId[r.prayer_id]}
                        </div>
                      )}
                      <div className="mt-2 text-[11px] text-zinc-400 tabular-nums">Last reported {fmtDateTime(r.latest_at)}</div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => openDeletePrompt([r.prayer_id])}
                        disabled={isDeleted}
                        data-testid={`misuse-delete-btn-${i}`}
                        title="Soft-delete this prayer and clear its reports"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                        Delete Prayer
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDismiss(r)}
                        disabled={dismissingId === r.prayer_id}
                        data-testid={`misuse-dismiss-btn-${i}`}
                        title="Dismiss the report(s) without deleting the prayer"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {dismissingId === r.prayer_id ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                        Delete Report
                      </button>
                      <button
                        type="button"
                        onClick={() => setNotifyRow(r)}
                        disabled={!canNotify}
                        title={canNotify ? 'Send a direct notification to this prayer’s owner' : 'Owner info unavailable'}
                        data-testid={`misuse-notify-btn-${i}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Mail size={14} />
                        Notify Owner
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <DeleteReasonModal
        open={!!pendingDelete}
        count={pendingDelete?.ids?.length || 0}
        working={deleting}
        onClose={() => (deleting ? null : setPendingDelete(null))}
        onConfirm={confirmDelete}
      />

      <NotifyOwnerModal
        open={!!notifyRow}
        row={notifyRow}
        storedReason={notifyRow ? reasonByPrayerId[notifyRow.prayer_id] : null}
        sending={notifySending}
        onClose={() => (notifySending ? null : setNotifyRow(null))}
        onSend={handleNotify}
      />
      {confirmDialog}
    </div>
  );
};

export default AdminMisuseReports;
